import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, BookOpen, Bot, Calculator, Briefcase, Building2, ChevronDown, Clock, Code2, Crown, FileSearch, FileText, HelpCircle, Layers, type LucideIcon, Menu, Plug, Scale, ScanSearch, Share2, Trophy, Users, Video, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import beatviewLogo from '@/assets/beatview-logo.svg';
import { APP_URL, DOCS_URL } from '@/data/marketingContent';
import { cn } from '@/lib/utils';

type NavItem = { name: string; href: string; description: string; icon: LucideIcon };
type NavMenu = { label: string; items: NavItem[]; promo: { eyebrow: string; title: string; href: string } };

const menus: NavMenu[] = [
  {
    label: 'Product',
    items: [
      { name: 'Resume screening', href: '/resume-screening', description: 'Score every application against your role criteria', icon: FileSearch },
      { name: 'AI video interviews', href: '/ai-interviews', description: 'Structured, timed interviews candidates take on their own', icon: Video },
      { name: 'AI assistants (MCP)', href: '/mcp', description: 'Run hiring from Claude, ChatGPT or Gemini', icon: Bot },
      { name: 'Candidate evidence', href: '/features', description: 'The resume line or answer behind every score', icon: ScanSearch },
      { name: 'Client sharing', href: '/features', description: 'Password-protected candidate links for clients', icon: Share2 },
      { name: 'Integrations', href: '/integrations', description: 'Works with most ATS platforms', icon: Plug },
    ],
    promo: { eyebrow: 'New · MCP server', title: 'Open roles and compare candidates from Claude, ChatGPT or Gemini', href: '/mcp' },
  },
  {
    label: 'Solutions',
    items: [
      { name: 'Recruitment agencies', href: '/for-recruiting-agencies', description: 'How agencies put the agent to work', icon: Building2 },
      { name: 'Permanent recruitment', href: '/solutions/permanent-recruitment', description: 'The same first review for every application', icon: Briefcase },
      { name: 'Contract & temp staffing', href: '/solutions/contract-temp-staffing', description: 'Structure for fast-moving desks', icon: Clock },
      { name: 'Executive search', href: '/solutions/executive-search', description: 'Evidence for high-stakes shortlists', icon: Crown },
      { name: 'RPO & embedded teams', href: '/solutions/rpo-embedded-teams', description: 'Consistent delivery across clients', icon: Users },
    ],
    promo: { eyebrow: 'Free calculator', title: 'What does screening cost your desk each month?', href: '/screening-cost-calculator' },
  },
  {
    label: 'Compare',
    items: [
      { name: 'Best AI recruiting software', href: '/guides/best-ai-recruiting-software', description: 'Tools for agencies, grouped by hiring stage', icon: Trophy },
      { name: 'Recruitment agency software', href: '/guides/recruitment-agency-software', description: 'Agency ATS and CRMs, and the AI layer beside them', icon: Layers },
      { name: 'Software pricing', href: '/guides/recruitment-software-pricing', description: 'What agency ATS and AI tools cost', icon: Calculator },
      { name: 'HireVue alternatives', href: '/alternatives/hirevue', description: 'Interview platforms with published pricing', icon: Scale },
      { name: 'Recruiting MCP servers', href: '/guides/recruiting-mcp-servers', description: 'Which hiring tools work with AI assistants', icon: Bot },
      { name: 'All comparisons', href: '/alternatives', description: 'Beatview versus other tools', icon: Scale },
    ],
    promo: { eyebrow: 'Free plan', title: 'Test Beatview on one live role before you switch anything', href: '/pricing' },
  },
  {
    label: 'Resources',
    items: [
      { name: 'What is an AI recruiting agent?', href: '/guides/what-is-an-ai-recruiting-agent', description: 'The plain-English guide', icon: BookOpen },
      { name: 'Guides', href: '/resources', description: 'AI recruiting, screening and pricing guides', icon: BookOpen },
      { name: 'Free templates', href: '/templates', description: 'Rubrics, forms, checklists and policies', icon: FileText },
      { name: 'Interview questions by role', href: '/interview-questions', description: 'Questions and scorecards for 72 roles', icon: HelpCircle },
      { name: 'One-way video interviews', href: '/one-way-video-interviews', description: 'How asynchronous interviews work', icon: Video },
      { name: 'Screening cost calculator', href: '/screening-cost-calculator', description: 'Free tool: hours and cost of CV screening', icon: Calculator },
      { name: 'Glossary', href: '/glossary', description: 'Recruitment and AI terms, defined', icon: BookOpen },
      { name: 'All questions', href: '/faq', description: 'Answers before you commit', icon: HelpCircle },
      { name: 'Documentation', href: DOCS_URL, description: 'Product docs and setup', icon: Code2 },
    ],
    promo: { eyebrow: 'Blog', title: 'Practical writing on agency screening and AI hiring', href: '/blog' },
  },
];

function SmartLink({ href, className, onClick, children, role }: { href: string; className?: string; onClick?: () => void; children: ReactNode; role?: string }) {
  if (href.startsWith('http')) return <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} role={role}>{children}</a>;
  if (href.includes('#')) return <a href={href} className={className} onClick={onClick} role={role}>{children}</a>;
  return <Link to={href} className={className} onClick={onClick} role={role}>{children}</Link>;
}

function DesktopMenu({ menu, open, onOpen, onClose }: { menu: NavMenu; open: boolean; onOpen: () => void; onClose: () => void }) {
  return (
    <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => (open ? onClose() : onOpen())} className={cn('inline-flex items-center gap-1 rounded-lg px-3 py-2 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue', open ? 'bg-bv-blue-wash text-bv-ink' : 'text-bv-ink2 hover:text-bv-ink')}>
        {menu.label}<ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {open && (
          <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3">
            <motion.div initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.98 }} transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }} className="grid w-[660px] grid-cols-[1fr_220px] gap-2 rounded-2xl border border-bv-blue-line bg-white p-2 shadow-[0_30px_80px_-30px_hsl(var(--navy)/0.4)]">
              <ul className="grid gap-0.5 p-1">
                {menu.items.map(item => (
                  <li key={item.name}>
                    <SmartLink href={item.href} onClick={onClose} className="group flex items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-bv-blue-wash focus:bg-bv-blue-wash focus:outline-none">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-bv-blue-line bg-white text-bv-blue transition-colors group-hover:border-transparent group-hover:bg-bv-ink group-hover:text-white"><item.icon className="h-4 w-4" /></span>
                      <span>
                        <span className="flex items-center gap-1 text-[14px] font-semibold text-bv-ink">{item.name}{item.href.startsWith('http') && <ArrowUpRight className="h-3 w-3 text-bv-mute" />}</span>
                        <span className="mt-0.5 block text-[13px] leading-5 text-bv-mute">{item.description}</span>
                      </span>
                    </SmartLink>
                  </li>
                ))}
              </ul>
              <SmartLink href={menu.promo.href} onClick={onClose} className="group relative flex flex-col justify-end overflow-hidden rounded-xl bg-bv-ink p-5 text-white">
                <span aria-hidden className="bv-grid-dark absolute inset-0" />
                <span aria-hidden className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[hsl(var(--hero-highlight-start)/0.45)] blur-3xl transition-transform duration-500 group-hover:scale-125" />
                <span className="relative text-[11px] font-semibold uppercase tracking-wider text-bv-blue-bright">{menu.promo.eyebrow}</span>
                <span className="relative mt-2 font-display text-lg font-semibold leading-snug">{menu.promo.title}</span>
                <span className="relative mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-white/80 group-hover:text-white">Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </SmartLink>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MarketingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<number>();
  const headerRef = useRef<HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); setOpenMenu(null); }, [location.pathname, location.hash]);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 12);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpenMenu(null);
      if (mobileOpen) { setMobileOpen(false); buttonRef.current?.focus(); }
    };
    const onPointerDown = (event: MouseEvent) => { if (!headerRef.current?.contains(event.target as Node)) setOpenMenu(null); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onPointerDown); };
  }, [mobileOpen]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const openDropdown = (label: string) => { window.clearTimeout(closeTimer.current); setOpenMenu(label); };
  const closeDropdown = () => { window.clearTimeout(closeTimer.current); closeTimer.current = window.setTimeout(() => setOpenMenu(null), 120); };
  const solid = scrolled || mobileOpen;
  const dropdown = (menu: NavMenu) => <DesktopMenu key={menu.label} menu={menu} open={openMenu === menu.label} onOpen={() => openDropdown(menu.label)} onClose={closeDropdown} />;

  return (
    <header ref={headerRef} className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4">
      <nav aria-label="Primary" className={cn('mx-auto flex h-16 max-w-[1240px] items-center justify-between rounded-2xl border px-3 transition-[background-color,border-color,box-shadow] duration-300 sm:px-4', solid ? 'border-bv-blue-line/80 bg-white/80 shadow-[0_12px_40px_-24px_hsl(var(--navy)/0.45)] backdrop-blur-xl' : 'border-transparent bg-transparent')}>
        <Link to="/" aria-label="Beatview home" className="flex items-center rounded-lg px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue"><img src={beatviewLogo} alt="Beatview" className="h-8 w-auto" /></Link>
        <div className="hidden items-center gap-0.5 lg:flex">
          {dropdown(menus[0])}
          {dropdown(menus[1])}
          <Link to="/pricing" className="rounded-lg px-3 py-2 text-[14px] font-medium text-bv-ink2 transition-colors hover:text-bv-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue">Pricing</Link>
          {dropdown(menus[2])}
          {dropdown(menus[3])}
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <a href={APP_URL} className="rounded-lg px-3 py-2 text-[14px] font-semibold text-bv-ink transition-colors hover:text-bv-blue">Sign in</a>
          <Button asChild size="sm" className="bv-shine h-10 rounded-xl px-4 text-[14px] font-semibold"><a href={APP_URL}>Start for free <ArrowRight className="h-3.5 w-3.5" /></a></Button>
        </div>
        <button ref={buttonRef} type="button" className="flex h-11 w-11 items-center justify-center rounded-xl text-bv-ink transition-colors hover:bg-bv-blue-wash lg:hidden" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} aria-controls="mobile-navigation" onClick={() => setMobileOpen(v => !v)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav id="mobile-navigation" aria-label="Mobile" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className="mx-auto mt-2 max-h-[calc(100dvh-96px)] max-w-[1240px] overflow-y-auto rounded-2xl border border-bv-blue-line bg-white p-3 shadow-bv-window lg:hidden">
            {menus.map(menu => (
              <div key={menu.label} className="border-b border-bv-blue-line py-2">
                <p className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-bv-mute">{menu.label}</p>
                <div className="grid gap-0.5 sm:grid-cols-2">
                  {menu.items.map(item => (
                    <SmartLink key={item.name} href={item.href} onClick={() => setMobileOpen(false)} className="flex min-h-11 items-center gap-3 rounded-xl px-2 py-2 text-[15px] font-medium text-bv-ink hover:bg-bv-blue-wash">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bv-blue-wash text-bv-blue"><item.icon className="h-4 w-4" /></span>{item.name}
                    </SmartLink>
                  ))}
                </div>
              </div>
            ))}
            <Link to="/pricing" className="mt-2 flex min-h-11 items-center rounded-xl px-2 text-[15px] font-semibold text-bv-ink hover:bg-bv-blue-wash">Pricing</Link>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button asChild variant="outline" className="h-11 rounded-xl border-bv-blue-line"><a href={APP_URL}>Sign in</a></Button>
              <Button asChild className="h-11 rounded-xl"><a href={APP_URL}>Start for free</a></Button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
